import type {
	AgentReportPage,
	AgentReportRow,
	AgentReportSort,
	AiCopilotMode,
	AiCopilotQualityReport,
	AiRoutingQualityReport,
	MembershipSummary,
	ReportSortDirection,
} from '@livechat/shared';
import { useEffect, useMemo, useState, type ReactNode } from 'react';

import {
	getAgentReport,
	getAgentReportExport,
	getAiCopilotQualityReport,
	getAiRoutingQualityReport,
} from '../api.js';
import { DateRangePicker, presetDateRange, type DateRangeValue } from './DateRangePicker.js';
import { ChatIssuesReport } from './ChatIssuesReport.js';
import { MemberAvatar } from './MemberAvatar.js';

const PAGE_SIZE = 25;

interface ReportMetric {
	kind: 'answered' | 'chat' | 'rating' | 'time' | 'unanswered' | 'waiting';
	label: string;
	tone: string;
	value: string;
}

interface SortableColumn {
	key: AgentReportSort;
	label: string;
}

const SORTABLE_COLUMNS: SortableColumn[] = [
	{ key: 'name', label: 'Agent' },
	{ key: 'chatCount', label: 'Chats' },
	{ key: 'timeOnline', label: 'Time Online' },
	{ key: 'averageWaitingTime', label: 'Avg waiting time' },
	{ key: 'averageChatTime', label: 'Avg chat time' },
	{ key: 'totalChatTime', label: 'Total chat time' },
	{ key: 'averageRating', label: 'Avg rating' },
];

function ReportMetricIcon({ kind }: { kind: ReportMetric['kind'] }) {
	const paths: Record<ReportMetric['kind'], ReactNode> = {
		answered: <path d="m5 12 4 4L19 6M3 12l4 4M11 12l4-4" />,
		chat: (
			<>
				<path d="M5 5h14v11H9l-4 3z" />
				<path d="M9 9h6M9 12h4" />
			</>
		),
		rating: (
			<path d="m12 3 2.7 5.5 6.1.9-4.4 4.3 1 6.1-5.4-2.9-5.4 2.9 1-6.1-4.4-4.3 6.1-.9z" />
		),
		time: (
			<>
				<circle cx="12" cy="12" r="8" />
				<path d="M12 8v5l3 2" />
			</>
		),
		unanswered: (
			<>
				<circle cx="12" cy="12" r="8" />
				<path d="m9 9 6 6M15 9l-6 6" />
			</>
		),
		waiting: (
			<>
				<circle cx="12" cy="12" r="8" />
				<path d="M12 7v5h4" />
			</>
		),
	};
	return (
		<svg aria-hidden="true" fill="none" viewBox="0 0 24 24">
			{paths[kind]}
		</svg>
	);
}

export function formatReportDuration(seconds?: number): string {
	if (seconds === undefined) return '—';
	if (seconds < 60) return `${seconds} sec`;
	const hours = Math.floor(seconds / 3600);
	const minutes = Math.floor((seconds % 3600) / 60);
	const remainder = seconds % 60;
	if (hours) {
		return [`${hours} hr`, minutes ? `${minutes} min` : '', remainder ? `${remainder} sec` : '']
			.filter(Boolean)
			.join(' ');
	}
	return remainder ? `${minutes} min ${remainder} sec` : `${minutes} min`;
}

function routingValueLabel(value: string): string {
	return value
		.split('_')
		.map((part) => part.charAt(0).toUpperCase() + part.slice(1))
		.join(' ');
}

export function formatClockDuration(seconds?: number): string {
	if (seconds === undefined) return '—';
	const hours = Math.floor(seconds / 3600);
	const minutes = Math.floor((seconds % 3600) / 60);
	const remainder = seconds % 60;
	return [hours, minutes, remainder].map((value) => String(value).padStart(2, '0')).join(':');
}

function csvCell(value: string | number): string {
	const text = String(value);
	return /[",\r\n]/.test(text) ? `"${text.replaceAll('"', '""')}"` : text;
}

export function agentReportCsv(rows: AgentReportRow[]): string {
	const headings = [
		'Agent',
		'Email',
		'Number of chats',
		'Time Online',
		'Average waiting time',
		'Average chat time',
		'Total chat time',
		'Average rating',
	];
	return [
		headings,
		...rows.map((row) => [
			row.displayName,
			row.email,
			row.chatCount,
			formatReportDuration(row.timeOnlineSeconds),
			row.averageWaitingSeconds === undefined
				? ''
				: formatReportDuration(row.averageWaitingSeconds),
			row.averageChatSeconds === undefined
				? ''
				: formatReportDuration(row.averageChatSeconds),
			formatReportDuration(row.totalChatSeconds),
			row.averageRating ?? '',
		]),
	]
		.map((row) => row.map(csvCell).join(','))
		.join('\r\n');
}

const COPILOT_MODE_LABELS: Record<AiCopilotMode, string> = {
	ask_ai: 'Ask AI',
	conversation_summary: 'Conversation summary',
	help_me_answer: 'Help me answer',
	suggest_reply: 'Suggest reply',
};

function formatMilliseconds(milliseconds?: number): string {
	if (milliseconds === undefined) return '—';
	return milliseconds < 1_000 ? `${milliseconds} ms` : `${(milliseconds / 1_000).toFixed(1)} sec`;
}

export function copilotReportCsv(report: AiCopilotQualityReport): string {
	return [
		[
			'Mode',
			'Runs',
			'Completed',
			'Failed',
			'Accepted',
			'Edited',
			'Rejected',
			'Helpful',
			'Not helpful',
			'Average latency',
			'Tokens',
		],
		...report.modes.map((mode) => [
			COPILOT_MODE_LABELS[mode.mode],
			mode.totalRuns,
			mode.completedRuns,
			mode.failedRuns,
			mode.acceptedRuns,
			mode.editedRuns,
			mode.rejectedRuns,
			mode.helpfulRuns,
			mode.unhelpfulRuns,
			formatMilliseconds(mode.averageDurationMs),
			mode.totalTokens,
		]),
	]
		.map((row) => row.map(csvCell).join(','))
		.join('\r\n');
}

export function routingReportCsv(report: AiRoutingQualityReport): string {
	return [
		['Routing overview'],
		['Analyses', report.overview.totalAnalyses],
		['Manual analyses', report.overview.manualAnalyses],
		['Automatic evaluations', report.overview.automaticAnalyses],
		['Automatic evaluations reviewed', report.overview.automaticReviewedAnalyses],
		['Completed', report.overview.completedAnalyses],
		['Failed', report.overview.failedAnalyses],
		['Reviewed', report.overview.reviewedAnalyses],
		['Accepted', report.overview.acceptedAnalyses],
		['Corrected', report.overview.correctedAnalyses],
		['Dismissed', report.overview.dismissedAnalyses],
		['Reviewed accuracy', report.overview.accuracyPercent ?? ''],
		['Average confidence', report.overview.averageConfidence ?? ''],
		['Average latency', formatMilliseconds(report.overview.averageDurationMs)],
		['Average queue wait', formatReportDuration(report.overview.averageQueueSeconds)],
		['Transfer candidates', report.overview.transferCandidates],
		['Matched later transfers', report.overview.matchedTransfers],
		['Matched transfer rate', report.overview.matchedTransferPercent ?? ''],
		[
			'Average time to matched transfer',
			formatReportDuration(report.overview.averageMatchedTransferSeconds),
		],
		['Tokens', report.overview.totalTokens],
		[],
		['Intent', 'Count', 'Average confidence', 'Positive', 'Neutral', 'Negative', 'Mixed'],
		...report.intents.map((intent) => [
			intent.intent,
			intent.count,
			intent.averageConfidence ?? '',
			intent.positiveCount,
			intent.neutralCount,
			intent.negativeCount,
			intent.mixedCount,
		]),
		[],
		['Suggested priority', 'Count', 'Accepted', 'Corrected reviews', 'Priority corrections'],
		...report.priorities.map((priority) => [
			priority.priority,
			priority.count,
			priority.acceptedCount,
			priority.correctedCount,
			priority.priorityCorrectionCount,
		]),
		[],
		['Recommended department', 'Recommendations', 'Accepted', 'Corrected'],
		...report.departments.map((department) => [
			department.departmentName,
			department.recommendedCount,
			department.acceptedCount,
			department.correctedCount,
		]),
	]
		.map((row) => row.map(csvCell).join(','))
		.join('\r\n');
}

function AgentIdentity({ agent }: { agent: AgentReportRow }) {
	return (
		<div className="agent-report-identity">
			<span className="agent-report-avatar">
				{agent.avatarEnabled ? (
					<MemberAvatar
						dataUrl={agent.avatarKind === 'uploaded' ? agent.avatarDataUrl : undefined}
						displayName={agent.displayName}
						enabled
						preset={agent.avatarPreset}
					/>
				) : (
					<span aria-hidden="true" className="agent-report-initial">
						{agent.displayName.slice(0, 1).toUpperCase()}
					</span>
				)}
			</span>
			<span>
				<strong>{agent.displayName}</strong>
				<small>{agent.email}</small>
			</span>
		</div>
	);
}

function SortHeader({
	column,
	onSort,
	order,
	sort,
}: {
	column: SortableColumn;
	onSort: (column: AgentReportSort) => void;
	order: ReportSortDirection;
	sort: AgentReportSort;
}) {
	const active = column.key === sort;
	return (
		<div
			aria-sort={active ? (order === 'asc' ? 'ascending' : 'descending') : 'none'}
			role="columnheader"
		>
			<button onClick={() => onSort(column.key)} type="button">
				<span>{column.label}</span>
				{active && <span aria-hidden="true" className={`sort-indicator active ${order}`} />}
			</button>
		</div>
	);
}

export type ReportSection = 'agent' | 'copilot' | 'issues' | 'routing';

export interface ReportNavigationItem {
	label: string;
	section: ReportSection;
}

export function reportNavigation(membership: MembershipSummary): ReportNavigationItem[] {
	const items: ReportNavigationItem[] = [{ label: 'Agent report', section: 'agent' }];
	return membership.role === 'owner'
		? [
				...items,
				{ label: 'AI Copilot quality', section: 'copilot' },
				{ label: 'AI routing quality', section: 'routing' },
				{ label: 'Chat classification', section: 'issues' },
			]
		: items;
}

export function TenantReports({
	membership,
	section,
}: {
	membership: MembershipSummary;
	section: ReportSection;
}) {
	const report = membership.role === 'owner' ? section : 'agent';
	const [range, setRange] = useState<DateRangeValue>(() => presetDateRange());
	const [sort, setSort] = useState<AgentReportSort>('name');
	const [order, setOrder] = useState<ReportSortDirection>('asc');
	const [page, setPage] = useState(1);
	const [result, setResult] = useState<AgentReportPage>();
	const [copilotResult, setCopilotResult] = useState<AiCopilotQualityReport>();
	const [routingResult, setRoutingResult] = useState<AiRoutingQualityReport>();
	const [loading, setLoading] = useState(true);
	const [exporting, setExporting] = useState(false);
	const [error, setError] = useState<string>();
	const timezone = useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC', []);

	useEffect(() => {
		if (report !== 'agent') return;
		let current = true;
		setLoading(true);
		setError(undefined);
		void getAgentReport(membership.tenantId, {
			...range,
			order,
			page,
			pageSize: PAGE_SIZE,
			sort,
			timezone,
		})
			.then((response) => {
				if (current) setResult(response);
			})
			.catch((caught: unknown) => {
				if (current) {
					setError(
						caught instanceof Error
							? caught.message
							: 'Could not load the agent report',
					);
				}
			})
			.finally(() => {
				if (current) setLoading(false);
			});
		return () => {
			current = false;
		};
	}, [membership.tenantId, order, page, range, report, sort, timezone]);

	useEffect(() => {
		if (report !== 'copilot') return;
		let current = true;
		setLoading(true);
		setError(undefined);
		void getAiCopilotQualityReport(membership.tenantId, { ...range, timezone })
			.then((response) => {
				if (current) setCopilotResult(response);
			})
			.catch((caught: unknown) => {
				if (current) {
					setError(
						caught instanceof Error
							? caught.message
							: 'Could not load the AI Copilot report',
					);
				}
			})
			.finally(() => {
				if (current) setLoading(false);
			});
		return () => {
			current = false;
		};
	}, [membership.tenantId, range, report, timezone]);

	useEffect(() => {
		if (report !== 'routing') return;
		let current = true;
		setLoading(true);
		setError(undefined);
		void getAiRoutingQualityReport(membership.tenantId, { ...range, timezone })
			.then((response) => {
				if (current) setRoutingResult(response);
			})
			.catch((caught: unknown) => {
				if (current) {
					setError(
						caught instanceof Error
							? caught.message
							: 'Could not load the AI routing report',
					);
				}
			})
			.finally(() => {
				if (current) setLoading(false);
			});
		return () => {
			current = false;
		};
	}, [membership.tenantId, range, report, timezone]);

	if (report === 'issues') return <ChatIssuesReport membership={membership} />;

	function changeSort(column: AgentReportSort): void {
		setPage(1);
		if (column === sort) {
			setOrder((current) => (current === 'asc' ? 'desc' : 'asc'));
			return;
		}
		setSort(column);
		setOrder(column === 'name' ? 'asc' : 'desc');
	}

	async function exportCsv(): Promise<void> {
		setExporting(true);
		setError(undefined);
		try {
			const csv =
				report === 'copilot' && copilotResult
					? copilotReportCsv(copilotResult)
					: report === 'routing' && routingResult
						? routingReportCsv(routingResult)
						: agentReportCsv(
								await getAgentReportExport(membership.tenantId, {
									...range,
									order,
									sort,
									timezone,
								}),
							);
			const blob = new Blob([`\uFEFF${csv}`], {
				type: 'text/csv;charset=utf-8',
			});
			const url = URL.createObjectURL(blob);
			const link = document.createElement('a');
			link.download = `${report === 'copilot' ? 'ai-copilot-quality' : report === 'routing' ? 'ai-routing-quality' : 'agent-report'}_${range.from}_to_${range.to}.csv`;
			link.href = url;
			link.click();
			URL.revokeObjectURL(url);
		} catch (caught) {
			setError(
				caught instanceof Error ? caught.message : 'Could not export the agent report',
			);
		} finally {
			setExporting(false);
		}
	}

	const metrics: ReportMetric[] = result
		? [
				{
					kind: 'chat',
					label: 'Total chats',
					tone: 'slate',
					value: result.overview.totalChats.toLocaleString(),
				},
				{
					kind: 'answered',
					label: 'Answered',
					tone: 'green',
					value: result.overview.answeredChats.toLocaleString(),
				},
				{
					kind: 'unanswered',
					label: 'Unanswered',
					tone: 'red',
					value: result.overview.unansweredChats.toLocaleString(),
				},
				{
					kind: 'waiting',
					label: 'Average waiting time',
					tone: 'orange',
					value: formatClockDuration(result.overview.averageWaitingSeconds),
				},
				{
					kind: 'time',
					label: 'Average chat time',
					tone: 'cyan',
					value: formatClockDuration(result.overview.averageChatSeconds),
				},
				{
					kind: 'time',
					label: 'Total chat time',
					tone: 'slate',
					value: formatClockDuration(result.overview.totalChatSeconds),
				},
				{
					kind: 'rating',
					label: 'Average rating',
					tone: 'blue',
					value: result.overview.averageRating?.toFixed(1) ?? '—',
				},
			]
		: [];
	const copilotMetrics: ReportMetric[] = copilotResult
		? [
				{
					kind: 'chat',
					label: 'Copilot runs',
					tone: 'slate',
					value: copilotResult.overview.totalRuns.toLocaleString(),
				},
				{
					kind: 'answered',
					label: 'Completed',
					tone: 'green',
					value: copilotResult.overview.completedRuns.toLocaleString(),
				},
				{
					kind: 'unanswered',
					label: 'Failed',
					tone: 'red',
					value: copilotResult.overview.failedRuns.toLocaleString(),
				},
				{
					kind: 'answered',
					label: 'Accepted or helpful',
					tone: 'blue',
					value: copilotResult.overview.acceptedOrHelpfulRuns.toLocaleString(),
				},
				{
					kind: 'time',
					label: 'Average latency',
					tone: 'cyan',
					value: formatMilliseconds(copilotResult.overview.averageDurationMs),
				},
				{
					kind: 'rating',
					label: 'Tokens used',
					tone: 'slate',
					value: copilotResult.overview.totalTokens.toLocaleString(),
				},
			]
		: [];
	const routingMetrics: ReportMetric[] = routingResult
		? [
				{
					kind: 'chat',
					label: 'Routing analyses',
					tone: 'slate',
					value: routingResult.overview.totalAnalyses.toLocaleString(),
				},
				{
					kind: 'answered',
					label: 'Completed',
					tone: 'green',
					value: routingResult.overview.completedAnalyses.toLocaleString(),
				},
				{
					kind: 'unanswered',
					label: 'Failed',
					tone: 'red',
					value: routingResult.overview.failedAnalyses.toLocaleString(),
				},
				{
					kind: 'chat',
					label: 'Automatic evaluations',
					tone: 'slate',
					value: routingResult.overview.automaticAnalyses.toLocaleString(),
				},
				{
					kind: 'answered',
					label: 'Automatic reviewed',
					tone: 'blue',
					value: routingResult.overview.automaticReviewedAnalyses.toLocaleString(),
				},
				{
					kind: 'rating',
					label: 'Matched transfers',
					tone: 'blue',
					value:
						routingResult.overview.matchedTransferPercent === undefined
							? '—'
							: `${routingResult.overview.matchedTransferPercent.toFixed(1)}%`,
				},
				{
					kind: 'time',
					label: 'Time to matched transfer',
					tone: 'orange',
					value: formatClockDuration(
						routingResult.overview.averageMatchedTransferSeconds,
					),
				},
				{
					kind: 'rating',
					label: 'Reviewed accuracy',
					tone: 'blue',
					value:
						routingResult.overview.accuracyPercent === undefined
							? '—'
							: `${routingResult.overview.accuracyPercent.toFixed(1)}%`,
				},
				{
					kind: 'rating',
					label: 'Average confidence',
					tone: 'cyan',
					value:
						routingResult.overview.averageConfidence === undefined
							? '—'
							: `${Math.round(routingResult.overview.averageConfidence * 100)}%`,
				},
				{
					kind: 'time',
					label: 'Average latency',
					tone: 'cyan',
					value: formatMilliseconds(routingResult.overview.averageDurationMs),
				},
				{
					kind: 'waiting',
					label: 'Queue wait at analysis',
					tone: 'orange',
					value: formatClockDuration(routingResult.overview.averageQueueSeconds),
				},
			]
		: [];
	const pageCount = Math.max(1, Math.ceil((result?.total ?? 0) / PAGE_SIZE));
	const first = result?.total ? (result.page - 1) * result.pageSize + 1 : 0;
	const last = result ? Math.min(result.total, result.page * result.pageSize) : 0;

	return (
		<section className="reports-workspace" aria-labelledby="reports-heading">
			<header className="reports-heading">
				<div>
					<h2 id="reports-heading">
						{report === 'agent'
							? 'Agent report'
							: report === 'copilot'
								? 'AI Copilot quality'
								: 'AI routing quality'}
					</h2>
					<p>Review team performance and service activity for {membership.tenantName}.</p>
				</div>
			</header>

			<div className="reports-toolbar">
				<DateRangePicker
					onChange={(updatedRange) => {
						setPage(1);
						setRange(updatedRange);
					}}
					value={range}
				/>
				<button
					className="primary-button report-export-button"
					disabled={
						exporting ||
						loading ||
						(report === 'agent'
							? !result
							: report === 'copilot'
								? !copilotResult
								: !routingResult)
					}
					onClick={() => void exportCsv()}
					type="button"
				>
					<svg aria-hidden="true" fill="none" viewBox="0 0 24 24">
						<path d="M12 3v12M7 10l5 5 5-5M5 20h14" />
					</svg>
					<span>{exporting ? 'Preparing CSV…' : 'Export CSV'}</span>
				</button>
			</div>

			{error && <div className="alert error reports-alert">{error}</div>}

			<div className="report-metrics" aria-busy={loading}>
				{(report === 'agent'
					? metrics
					: report === 'copilot'
						? copilotMetrics
						: routingMetrics
				).map((metric) => (
					<div className="report-metric" key={metric.label}>
						<span>
							<strong>{metric.value}</strong>
							<small>{metric.label}</small>
						</span>
						<span className={`report-metric-icon report-metric-${metric.tone}`}>
							<ReportMetricIcon kind={metric.kind} />
						</span>
					</div>
				))}
				{!(report === 'agent'
					? result
					: report === 'copilot'
						? copilotResult
						: routingResult) &&
					loading &&
					Array.from({ length: 7 }, (_, index) => (
						<div className="report-metric report-metric-loading" key={index} />
					))}
			</div>

			{report === 'agent' && (
				<section className="agent-report-section" aria-labelledby="agent-report-heading">
					<header className="table-section-heading">
						<div>
							<h3 id="agent-report-heading">Agent performance</h3>
						</div>
						<span className="table-section-count">{result?.total ?? 0}</span>
					</header>

					<div className="table-card paginated-table">
						<div
							className="agent-report-table paginated-table-scroll"
							role="table"
							aria-busy={loading}
						>
							<div
								className="agent-report-row agent-report-head standard-table-head"
								role="row"
							>
								{SORTABLE_COLUMNS.map((column) => (
									<SortHeader
										column={column}
										key={column.key}
										onSort={changeSort}
										order={order}
										sort={sort}
									/>
								))}
							</div>
							{result?.items.map((agent) => (
								<div
									className="table-row agent-report-row"
									key={agent.id}
									role="row"
								>
									<div role="cell">
										<AgentIdentity agent={agent} />
									</div>
									<div role="cell">{agent.chatCount.toLocaleString()}</div>
									<div role="cell">
										{formatReportDuration(agent.timeOnlineSeconds)}
									</div>
									<div role="cell">
										{formatReportDuration(agent.averageWaitingSeconds)}
									</div>
									<div role="cell">
										{formatReportDuration(agent.averageChatSeconds)}
									</div>
									<div role="cell">
										{formatReportDuration(agent.totalChatSeconds)}
									</div>
									<div role="cell">{agent.averageRating?.toFixed(1) ?? '—'}</div>
								</div>
							))}
							{!loading && !result?.items.length && (
								<div className="agent-report-empty" role="row">
									<div role="cell">
										<strong>No agents found</strong>
										<span>Active team members will appear in this report.</span>
									</div>
								</div>
							)}
							{loading && !result && (
								<div className="agent-report-empty" role="row">
									<div role="cell">Loading agent report…</div>
								</div>
							)}
						</div>
						<footer className="data-pagination report-pagination">
							<span>
								{first}–{last} of {result?.total ?? 0} agents
							</span>
							<div>
								<button
									aria-label="Previous page"
									disabled={page <= 1 || loading}
									onClick={() => setPage((current) => Math.max(1, current - 1))}
									type="button"
								>
									‹
								</button>
								<span aria-live="polite">
									Page {page} of {pageCount}
								</span>
								<button
									aria-label="Next page"
									disabled={page >= pageCount || loading}
									onClick={() =>
										setPage((current) => Math.min(pageCount, current + 1))
									}
									type="button"
								>
									›
								</button>
							</div>
						</footer>
					</div>
				</section>
			)}

			{report === 'copilot' && (
				<section className="agent-report-section" aria-labelledby="copilot-report-heading">
					<header className="table-section-heading">
						<div>
							<h3 id="copilot-report-heading">Copilot quality by capability</h3>
							<p>
								Aggregate usage and staff feedback; no prompts or conversation
								copies are retained.
							</p>
						</div>
					</header>
					<div
						className="table-card copilot-report-table"
						role="table"
						aria-busy={loading}
					>
						<div className="copilot-report-row standard-table-head" role="row">
							<div role="columnheader">Capability</div>
							<div role="columnheader">Runs</div>
							<div role="columnheader">Completed</div>
							<div role="columnheader">Failed</div>
							<div role="columnheader">Positive</div>
							<div role="columnheader">Edited</div>
							<div role="columnheader">Negative</div>
							<div role="columnheader">Avg latency</div>
							<div role="columnheader">Tokens</div>
						</div>
						{copilotResult?.modes.map((mode) => (
							<div
								className="table-row copilot-report-row"
								key={mode.mode}
								role="row"
							>
								<div role="cell">
									<strong>{COPILOT_MODE_LABELS[mode.mode]}</strong>
								</div>
								<div role="cell">{mode.totalRuns.toLocaleString()}</div>
								<div role="cell">{mode.completedRuns.toLocaleString()}</div>
								<div role="cell">{mode.failedRuns.toLocaleString()}</div>
								<div role="cell">
									{(mode.acceptedRuns + mode.helpfulRuns).toLocaleString()}
								</div>
								<div role="cell">{mode.editedRuns.toLocaleString()}</div>
								<div role="cell">
									{(mode.rejectedRuns + mode.unhelpfulRuns).toLocaleString()}
								</div>
								<div role="cell">{formatMilliseconds(mode.averageDurationMs)}</div>
								<div role="cell">{mode.totalTokens.toLocaleString()}</div>
							</div>
						))}
						{!loading && !copilotResult?.modes.length && (
							<div className="agent-report-empty" role="row">
								<div role="cell">
									<strong>No Copilot activity</strong>
									<span>Runs in the selected period will appear here.</span>
								</div>
							</div>
						)}
					</div>
				</section>
			)}

			{report === 'routing' && (
				<section className="agent-report-section" aria-labelledby="routing-report-heading">
					<header className="table-section-heading">
						<div>
							<h3 id="routing-report-heading">Routing recommendation quality</h3>
							<p>
								Aggregate classifications and staff corrections; no transcript
								copies or hidden reasoning are returned.
							</p>
						</div>
					</header>
					<div
						className="table-card routing-report-table"
						role="table"
						aria-busy={loading}
					>
						<div className="routing-intent-row standard-table-head" role="row">
							<div role="columnheader">Intent</div>
							<div role="columnheader">Analyses</div>
							<div role="columnheader">Avg confidence</div>
							<div role="columnheader">Positive</div>
							<div role="columnheader">Neutral</div>
							<div role="columnheader">Negative</div>
							<div role="columnheader">Mixed</div>
						</div>
						{routingResult?.intents.map((intent) => (
							<div
								className="table-row routing-intent-row"
								key={intent.intent}
								role="row"
							>
								<div role="cell">
									<strong>{intent.intent}</strong>
								</div>
								<div role="cell">{intent.count.toLocaleString()}</div>
								<div role="cell">
									{intent.averageConfidence === undefined
										? '—'
										: `${Math.round(intent.averageConfidence * 100)}%`}
								</div>
								<div role="cell">{intent.positiveCount.toLocaleString()}</div>
								<div role="cell">{intent.neutralCount.toLocaleString()}</div>
								<div role="cell">{intent.negativeCount.toLocaleString()}</div>
								<div role="cell">{intent.mixedCount.toLocaleString()}</div>
							</div>
						))}
						{!loading && !routingResult?.intents.length && (
							<div className="agent-report-empty" role="row">
								<div role="cell">
									<strong>No routing analysis yet</strong>
									<span>
										Reviewed recommendations in the selected period will appear
										here.
									</span>
								</div>
							</div>
						)}
					</div>
					<section
						className="routing-department-quality"
						aria-labelledby="routing-departments-heading"
					>
						<h3 id="routing-departments-heading">Department recommendations</h3>
						<div className="table-card routing-department-table" role="table">
							<div className="routing-department-row standard-table-head" role="row">
								<div role="columnheader">Department</div>
								<div role="columnheader">Recommended</div>
								<div role="columnheader">Accepted</div>
								<div role="columnheader">Corrected</div>
							</div>
							{routingResult?.departments.map((department) => (
								<div
									className="table-row routing-department-row"
									key={department.departmentId ?? 'none'}
									role="row"
								>
									<div role="cell">
										<strong>{department.departmentName}</strong>
									</div>
									<div role="cell">
										{department.recommendedCount.toLocaleString()}
									</div>
									<div role="cell">
										{department.acceptedCount.toLocaleString()}
									</div>
									<div role="cell">
										{department.correctedCount.toLocaleString()}
									</div>
								</div>
							))}
						</div>
					</section>
					<section
						className="routing-department-quality"
						aria-labelledby="routing-priorities-heading"
					>
						<h3 id="routing-priorities-heading">Suggested priority calibration</h3>
						<div className="table-card routing-department-table" role="table">
							<div className="routing-priority-row standard-table-head" role="row">
								<div role="columnheader">Suggested priority</div>
								<div role="columnheader">Analyses</div>
								<div role="columnheader">Accepted</div>
								<div role="columnheader">Corrected reviews</div>
								<div role="columnheader">Priority corrections</div>
							</div>
							{routingResult?.priorities.map((priority) => (
								<div
									className="table-row routing-priority-row"
									key={priority.priority}
									role="row"
								>
									<div role="cell">
										<strong>{routingValueLabel(priority.priority)}</strong>
									</div>
									<div role="cell">{priority.count.toLocaleString()}</div>
									<div role="cell">{priority.acceptedCount.toLocaleString()}</div>
									<div role="cell">
										{priority.correctedCount.toLocaleString()}
									</div>
									<div role="cell">
										{priority.priorityCorrectionCount.toLocaleString()}
									</div>
								</div>
							))}
						</div>
					</section>
				</section>
			)}
		</section>
	);
}
